/**
 * 登录页面
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authAPI } from '@/services/authApi';
import { useAuthStore } from '@/stores/auth';
import { Loader2 } from 'lucide-react';

export function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  
  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login(formData);

      if (response.success && response.data) {
        const { user, accessToken, refreshToken, permissionInfo } = response.data;

        // 保存用户信息和Token
        setAuth(user, accessToken, refreshToken);

        // 保存VIP权限信息到localStorage
        if (permissionInfo) {
          localStorage.setItem('vipPermissions', JSON.stringify(permissionInfo));
        }

        navigate('/tools');
      } else {
        setError(response.message || '登录失败');
      }
    } catch (err: any) {
      setError(err.message || '登录失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="flex-1 flex items-center justify-center w-full max-w-md">
        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">MCP Hub</CardTitle>
            <CardDescription className="text-center">
              登录到多用户工具管理平台
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="usernameOrEmail">用户名或邮箱</Label>
                <Input
                  id="usernameOrEmail"
                  type="text"
                  placeholder="admin"
                  value={formData.usernameOrEmail}
                  onChange={(e) => setFormData({ ...formData, usernameOrEmail: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">密码</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                登录
              </Button>

              <div className="text-sm text-center text-gray-600 dark:text-gray-400">
                还没有账号？
                <Link to="/register" className="ml-1 text-blue-600 hover:underline dark:text-blue-400">
                  立即注册
                </Link>
              </div>

              <div className="text-sm text-center">
                <Link to="/forgot-password" className="text-gray-500 hover:text-blue-600 hover:underline dark:text-gray-400">
                  忘记密码？
                </Link>
              </div>
            </CardFooter>
          </form>
        </Card>
      </div>

      {/* 备案信息 */}
      <footer className="py-4 text-center text-sm text-gray-500 dark:text-gray-400 space-y-1">
        <p>网站域名：yikaihui.com</p>
        <p>
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            京ICP备17037227号-1
          </a>
        </p>
      </footer>
    </div>
  );
}

